import { EmptyTablePlaceholder } from '@openx/components-core/lib/TablePlaceholder/EmptyTablePlaceholder.tsx';
import { SelectableTable } from '@openx/components-table/SelectableTable/SelectableTable.tsx';
import type { TableProps } from '@openx/components-table/Table.tsx';
import { Table, TableName } from '@openx/components-table/Table.tsx';
import type { ListLalSegment } from '@openx/types/segments.ts';
import type { ToggleSelect } from '@openx/types/selectableTable.ts';
import type { AllTableCriteria, ChangeCriteriaHandler, CriteriaDimension } from '@openx/types/tableCriteria.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SegmentsListFilters } from '../../types.ts';
import { SegmentsTopBar } from '../SegmentsList/SegmentsTopBar.tsx';
import { useSegmentContext } from '../utils/context/SegmentContext.tsx';

type Props = {
  segments: ListLalSegment[];
  loading: boolean;
  criteria: AllTableCriteria<SegmentsListFilters>;
  selectedSegments: ListLalSegment[];
  handleCriteriaChange: ChangeCriteriaHandler<CriteriaDimension, SegmentsListFilters>;
  handleFiltersChange: (filters: { dimension: typeof CriteriaDimension.FILTERS; value: SegmentsListFilters }) => void;
  handleSelectionChange: ToggleSelect<ListLalSegment>;
};

const SegmentsSelectableTable = <T extends Partial<ListLalSegment>>() =>
  SelectableTable<T, SegmentsListFilters, TableProps<T, SegmentsListFilters>>(Table);

export const LalSegmentList = ({
  segments,
  criteria,
  loading,
  handleCriteriaChange,
  handleFiltersChange,
  handleSelectionChange,
  selectedSegments,
}: Props) => {
  const { t } = useTranslation();
  const { useLalSegmentTableFetch } = useSegmentContext();

  const { columns, filtersConfig, rowActions } = useLalSegmentTableFetch();

  const LalSegmentsTable = useMemo(() => SegmentsSelectableTable<ListLalSegment>(), []);

  const isTableEmpty = !loading && !criteria.filters && segments.length === 0;

  return (
    <>
      <SegmentsTopBar filtersConfig={filtersConfig} criteria={criteria} onChange={handleFiltersChange} />
      {isTableEmpty ? (
        <EmptyTablePlaceholder />
      ) : (
        <LalSegmentsTable
          name={TableName.LAL_SEGMENTS}
          data={segments}
          columns={columns}
          rowActions={rowActions}
          criteria={criteria}
          loading={loading}
          onCriteriaChange={handleCriteriaChange}
          noItemsLabel={() => t('No LAL segments found')}
          selectedItems={selectedSegments}
          toggleSelect={handleSelectionChange}
        />
      )}
    </>
  );
};
